import { useCallback, useEffect, useRef } from "react";
import { BlsMessage, parseBlsMessage } from "../domain/blsChannel";
import type { BoardRelay } from "./useBoardRelay";
import { useBlsSession } from "./useBlsSession";
import type { BlsChannel } from "./useBlsSession";

const encoder = new TextEncoder();

/** Who is on each end of the standalone board. */
export type BoardRole = "therapist" | "patient";

/** The identities the authority rule reads, as on a call. */
export const BOARD_IDENTITIES: Record<BoardRole, string> = {
    therapist: "therapist-board",
    patient: "guest-board",
};

type Envelope = { from: string; payload: string };

/** One name per session, so two boards open in one browser never cross. */
export function boardChannelName(sessionId: string): string {
    return `alchely-bls-board-${sessionId}`;
}

/**
 * Bilateral stimulation between two windows of one browser.
 *
 * The standalone board runs outside any call — in the consulting room, on the
 * therapist's computer — and the patient's screen is a second window on a
 * monitor turned towards them. Both windows belong to the same browser, so a
 * BroadcastChannel carries the messages: nothing leaves the machine, and no
 * server is involved.
 *
 * Packets go through the same validation as on a call, and the same session
 * logic, so the board behaves exactly like the call does.
 *
 * When the therapist shares the board with a patient elsewhere, `relay` is the
 * board's mailbox on our server and every message goes through it as well:
 * one set, on the window beside the therapist and on the patient's device.
 */
export function useBroadcastBlsChannel(sessionId: string, role: BoardRole, relay: BoardRelay | null = null): BlsChannel {
    const identity = BOARD_IDENTITIES[role];
    // Opened and closed by the effect below rather than memoised: a channel
    // closed by one mount must never be the one the next mount writes to.
    const channelRef = useRef<BroadcastChannel | null>(null);
    const relayRef = useRef<BoardRelay | null>(relay);
    relayRef.current = relay;

    const send = useCallback(
        (message: BlsMessage) => {
            const payload = JSON.stringify(message);
            const envelope: Envelope = { from: identity, payload };
            channelRef.current?.postMessage(envelope);
            return relayRef.current?.post(payload);
        },
        [identity]
    );

    const readIdentity = useCallback(() => identity, [identity]);
    const session = useBlsSession(send, readIdentity);
    const { receive, announce, canControl } = session;

    useEffect(() => {
        if (typeof BroadcastChannel === "undefined") return;
        const channel = new BroadcastChannel(boardChannelName(sessionId));
        channelRef.current = channel;

        const onMessage = (event: MessageEvent<Envelope>) => {
            const { from, payload } = event.data ?? {};
            if (typeof from !== "string" || typeof payload !== "string") return;
            const message = parseBlsMessage(encoder.encode(payload));
            if (message) receive(message, from);
        };
        channel.addEventListener("message", onMessage);
        announce();

        /*
         * Closing the board ends the set on the patient's window: a stimulus
         * with nobody left to stop it is the one state this tool must never
         * reach.
         */
        const leave = () => {
            if (canControl) send({ kind: "stop" });
        };
        window.addEventListener("pagehide", leave);

        return () => {
            window.removeEventListener("pagehide", leave);
            leave();
            channel.removeEventListener("message", onMessage);
            channel.close();
            channelRef.current = null;
        };
    }, [announce, canControl, receive, send, sessionId]);

    useEffect(() => {
        if (!relay) return;
        const stopListening = relay.subscribe((payload, sender) => {
            const message = parseBlsMessage(encoder.encode(payload));
            if (message) receive(message, sender);
        });
        // A patient opening the link mid-set is put in step with it.
        const stopArrivals = relay.onArrival(announce);
        announce();
        return () => {
            stopListening();
            stopArrivals();
        };
    }, [announce, receive, relay]);

    return session;
}
